import { Dialog, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import {
  formatBaasStableAmount,
  getBaasStableMetadataEntries,
  getBaasStableMetadataLabelKey,
} from "../../../../../Services/baasStablesApi";
import { CrossborderCopyButton } from "../../../Crossborder/components/CrossborderCopyButton";
import { BaasStableOperationBadge, BaasStableStatusBadge } from "./BaasStableStatusBadge";

const DETAIL_TABS = [
  { id: "details", labelKey: "baas.stables.detail.tabs.details" },
  { id: "metadata", labelKey: "baas.stables.detail.tabs.metadata" },
];

const PANEL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !max-h-[90vh] !w-[calc(100%-2rem)] !max-w-3xl";

const DetailRow = ({ label, value, mono = false, copyValue }) => {
  if (value == null || value === "") return null;

  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-100 py-2.5 last:border-b-0 dark:border-brand-dark-border sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <div className="flex min-w-0 items-center gap-1 sm:justify-end">
        <span
          className={`text-sm font-medium text-brand-dark dark:text-gray-100 sm:text-right ${mono ? "font-mono text-xs break-all" : ""}`}
        >
          {value}
        </span>
        {copyValue ? (
          <CrossborderCopyButton
            value={copyValue}
            ariaLabel={intl.get("baas.stables.metadata.copyValue", { field: label })}
          />
        ) : null}
      </div>
    </div>
  );
};

const BaasStableDetailTabs = ({ activeTab, onTabChange }) => (
  <div
    className="extract-view-tabs extract-view-tabs--inline border-b border-gray-100 px-4 dark:border-brand-dark-border sm:px-5"
    role="tablist"
    aria-label={intl.get("baas.stables.detail.title")}
  >
    {DETAIL_TABS.map((tab) => {
      const isActive = activeTab === tab.id;
      return (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={isActive}
          className={`extract-view-tab ${isActive ? "extract-view-tab--active" : ""}`}
          onClick={() => onTabChange(tab.id)}
        >
          {intl.get(tab.labelKey)}
        </button>
      );
    })}
  </div>
);

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
};

const getMetadataFieldLabel = (fieldKey) => {
  const labelKey = getBaasStableMetadataLabelKey(fieldKey);
  const label = intl.get(labelKey);
  return label === labelKey ? fieldKey : label;
};

export const BaasStableDetailDrawer = ({ transaction, onClose }) => {
  const [activeTab, setActiveTab] = useState("details");
  const isOpen = Boolean(transaction);

  useEffect(() => {
    setActiveTab("details");
  }, [transaction?.id]);

  if (!transaction) {
    return null;
  }

  const metadataEntries = getBaasStableMetadataEntries(transaction);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ className: PANEL_CLASS }}
    >
      <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border sm:px-5">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            {intl.get("baas.stables.detail.title")}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h3 className="truncate font-mono text-sm text-brand-dark dark:text-gray-100">
              #{transaction.referenceId}
            </h3>
            <BaasStableStatusBadge status={transaction.status} />
            <BaasStableOperationBadge operation={transaction.operation} />
          </div>
        </div>
        <IconButton aria-label={intl.get("words.close")} onClick={onClose} size="small" className="!-mr-1">
          <MdClose />
        </IconButton>
      </div>

      <BaasStableDetailTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
        {activeTab === "details" ? (
          <>
            <p className="dashboard-section-label mb-2">
              {intl.get("baas.stables.detail.overview")}
            </p>
            <div className="app-card mb-5 !p-0 !shadow-none">
              <div className="px-4">
                <DetailRow
                  label={intl.get("baas.stables.columns.client")}
                  value={transaction.clientName}
                />
                <DetailRow
                  label={intl.get("baas.stables.columns.operation")}
                  value={intl.get(`baas.stables.operation.${transaction.operation}`)}
                />
                <DetailRow
                  label={intl.get("baas.stables.columns.asset")}
                  value={transaction.asset}
                />
                <DetailRow
                  label={intl.get("baas.stables.columns.amount")}
                  value={formatBaasStableAmount(transaction.amount, transaction.asset)}
                />
                <DetailRow
                  label={intl.get("baas.stables.detail.network")}
                  value={transaction.network}
                />
                <DetailRow
                  label={intl.get("baas.stables.columns.date")}
                  value={formatDateTime(transaction.createdAt)}
                />
                {transaction.processedAt ? (
                  <DetailRow
                    label={intl.get("baas.stables.detail.processedAt")}
                    value={formatDateTime(transaction.processedAt)}
                  />
                ) : null}
                <DetailRow
                  label={intl.get("baas.stables.detail.id")}
                  value={transaction.id}
                  mono
                  copyValue={transaction.id}
                />
              </div>
            </div>

            <p className="dashboard-section-label mb-2">
              {intl.get("baas.stables.detail.blockchain")}
            </p>
            <div className="app-card !p-0 !shadow-none">
              <div className="px-4">
                <DetailRow
                  label={intl.get("baas.stables.metadata.fields.wallet_from")}
                  value={transaction.metadata?.wallet_from}
                  mono
                  copyValue={transaction.metadata?.wallet_from}
                />
                <DetailRow
                  label={intl.get("baas.stables.metadata.fields.wallet_to")}
                  value={transaction.metadata?.wallet_to}
                  mono
                  copyValue={transaction.metadata?.wallet_to}
                />
                <DetailRow
                  label={intl.get("baas.stables.metadata.fields.tx_hash")}
                  value={transaction.metadata?.tx_hash}
                  mono
                  copyValue={transaction.metadata?.tx_hash}
                />
                <DetailRow
                  label={intl.get("baas.stables.metadata.fields.confirmations")}
                  value={transaction.metadata?.confirmations}
                />
                <DetailRow
                  label={intl.get("baas.stables.metadata.fields.gas_used")}
                  value={transaction.metadata?.gas_used}
                />
                <DetailRow
                  label={intl.get("baas.stables.metadata.fields.initiated_by")}
                  value={transaction.metadata?.initiated_by}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="dashboard-section-label mb-2">
              {intl.get("baas.stables.metadata.title")}
            </p>
            {metadataEntries.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {intl.get("baas.stables.metadata.empty")}
              </p>
            ) : (
              <div className="app-card !p-0 !shadow-none">
                <div className="px-4">
                  {metadataEntries.map(({ key, value }) => (
                    <DetailRow
                      key={key}
                      label={getMetadataFieldLabel(key)}
                      value={String(value)}
                      mono={key.includes("hash") || key.includes("wallet") || key.includes("id")}
                      copyValue={
                        key.includes("hash") || key.includes("wallet") || key.includes("id")
                          ? String(value)
                          : undefined
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
